import React from 'react';
import { useRecoilValue } from 'recoil';

import type * as api from '@gitbook/api-client';
import { intlPluralizeString } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, ButtonGroup, type ButtonProps } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useNavigation, useNavigationState } from '../../navigation';
import { getSerializedTableCellKey, isCommentNodeHoveredSelector } from '../../state';
import { useListPaginatedComments } from './useListPaginatedComments';

interface CommentsMap {
    [key: string]: api.Comment[] | undefined;
}

const CommentsContext = React.createContext<CommentsMap | null>(null);

export interface UseCommentsProps {
    /** The node (e.g. block) to read and write comments on */
    nodeKey: string;
    /** Table cell details if the comment is on a table cell */
    tableCell?: api.TableCellTarget;
}

export interface UseCommentsReturn {
    /** Whether there are comments on this node */
    hasComments: boolean;
    /** Number of total comments including replies */
    numberOfComments: number;
    /** Handler to open existing comments */
    onPressOpenNodeComment: () => void;
    /** Handler to create a new comment */
    onPressNewComment: () => void;
    /** Is a comment linked to this element currently hovered */
    isHovered: boolean;
    /** Is a comment linked to this element currently navigated to */
    isOpened: boolean;
}

export interface CommentButtonProps {
    /** Whether there are existing comments */
    hasComments: boolean;
    /** Number of comments to display */
    numberOfComments: number;
    /** Handler when clicking the main button */
    onPress: () => void;
    /** Handler when clicking the "add new comment" button (only shown when hasComments is true) */
    onPressNewComment: () => void;
    /** Kind of the button */
    kind?: ButtonProps['kind'];
}

/**
 * A context provider for comments on a document. Returns a stable ref to a map of node
 * keys to comments.
 */
export function DocumentCommentsProvider(props: {
    skip: boolean;
    spaceId: string;
    pageId: string;
    changeRequestId?: string;
    children: React.ReactNode;
}): React.ReactElement {
    const { skip, spaceId, pageId, changeRequestId, children } = props;

    const [comments] = useListPaginatedComments({
        spaceId,
        changeRequestId,
        pageId,
        status: 'open',
        skip,

        // We don't want to suspense here because we don't want to block the UI
        // when fetching comments.
        suspense: false,
    });

    const commentsByKey = React.useMemo<CommentsMap>(() => {
        const _commentsByKey: CommentsMap = {};
        if (!comments.length) {
            return _commentsByKey;
        }

        for (const comment of comments) {
            if (!comment.target.node) {
                continue;
            }

            if (!_commentsByKey[comment.target.node.id]) {
                _commentsByKey[comment.target.node.id] = [comment];
            } else {
                _commentsByKey[comment.target.node.id]?.push(comment);
            }
        }

        return _commentsByKey;
    }, [comments]);

    return <CommentsContext.Provider value={commentsByKey}>{children}</CommentsContext.Provider>;
}

/**
 * Hook to provide comment state and handlers for any block or node.
 */
export function useDocumentNodeComments({
    nodeKey,
    tableCell,
}: UseCommentsProps): UseCommentsReturn {
    const { navigate } = useNavigation();
    const { screen } = useNavigationState();

    const commentsByKey = React.useContext(CommentsContext);
    assertIsDefined(commentsByKey, 'useComments must be used within a CommentsContextProvider');
    const blockComments = commentsByKey?.[nodeKey];

    const comments = React.useMemo(() => {
        if (!blockComments) {
            return [];
        }
        return tableCell?.record && tableCell?.definition
            ? blockComments.filter(
                  (comment) =>
                      comment.target.tableCell &&
                      comment.target.tableCell.record === tableCell?.record &&
                      comment.target.tableCell.definition === tableCell?.definition
              )
            : blockComments;
    }, [blockComments, tableCell?.record, tableCell?.definition]);

    // Memoize filtered comments to avoid recalculating on every render
    const openComments = React.useMemo(
        () => comments.filter((comment: api.Comment) => comment.status === 'open'),
        [comments]
    );

    const hasComments = React.useMemo(() => comments.length > 0, [comments.length]);
    const leadingComment = comments?.[0];

    /** Count all open comments + any of their replies */
    const numberOfComments = React.useMemo(
        () => openComments.reduce((acc, curr) => acc + 1 + (curr.replies || 0), 0),
        [openComments]
    );

    // Hover state: check whether a comment attached to this node is hovered
    const isHovered = useRecoilValue(
        isCommentNodeHoveredSelector(getSerializedTableCellKey(tableCell) || nodeKey)
    );

    // Opened state: check if a comment attached to this node is in URL/selected/navigated to
    const isOpened = React.useMemo(() => {
        if (screen.type !== 'space' || screen.sideSheet?.type !== 'comments') {
            return false;
        }

        if (screen.sideSheet?.tableCell) {
            const openRecord = screen.sideSheet.tableCell.record;
            const openDefinition = screen.sideSheet.tableCell.definition;
            const currentRecord = tableCell?.record;
            const currentDefinition = tableCell?.definition;
            if (openRecord === currentRecord && openDefinition === currentDefinition) {
                return true;
            }
            return false;
        } else {
            return screen.sideSheet?.node === nodeKey;
        }
    }, [screen, nodeKey, tableCell]);

    const onPressOpenNodeComment = React.useCallback(() => {
        if (!leadingComment || screen.type !== 'space') return;

        navigate({
            screen: {
                type: 'space',
                space: leadingComment.target.space,
                // Preserve other space screen properties if we're currently in a space
                ...(screen.type === 'space'
                    ? {
                          page: screen.page,
                          organization: screen.organization,
                          mode: screen.mode,
                          revision: screen.revision,
                          reusableContent: screen.reusableContent,
                          changeRequest:
                              'changeRequest' in screen ? screen.changeRequest : undefined,
                      }
                    : { page: '' }),
                sideSheet: {
                    type: 'comments',
                    node: leadingComment.target.node?.id ?? undefined,
                    comment: leadingComment.id,
                    ...(tableCell
                        ? {
                              tableCell: {
                                  record: tableCell.record,
                                  definition: tableCell.definition,
                              },
                          }
                        : {}),
                },
            },
        });
    }, [leadingComment, navigate, screen, tableCell]);

    const onPressNewComment = React.useCallback(() => {
        if (screen.type !== 'space') {
            // Can only create comments in space screens
            return;
        }

        navigate({
            screen: {
                ...screen,
                sideSheet: {
                    type: 'comments',
                    node: nodeKey,
                    context: 'post',
                    comment: undefined,
                    ...(tableCell
                        ? {
                              tableCell: {
                                  record: tableCell.record,
                                  definition: tableCell.definition,
                              },
                          }
                        : {}),
                },
            },
        });
    }, [navigate, screen, nodeKey, tableCell]);

    return {
        hasComments,
        numberOfComments,
        onPressOpenNodeComment,
        onPressNewComment,
        isHovered,
        isOpened,
    };
}

/**
 * Comment button component that displays either a simple button or a button group
 * depending on whether there are existing comments.
 */
export const DocumentNodeCommentButton = React.memo<CommentButtonProps>(
    function DocumentNodeCommentButton({
        hasComments,
        numberOfComments,
        onPress,
        onPressNewComment,
        kind = 'secondary',
    }) {
        if (hasComments) {
            return (
                <ButtonGroup kind="segmented" separatorKind="secondary">
                    <Button
                        kind={kind}
                        data-exclude-from-copy
                        size="small"
                        leadingIcon={Icon.Comment}
                        title={`${numberOfComments} ${intlPluralizeString({
                            singular: 'Comment',
                            plural: 'Comments',
                            count: numberOfComments,
                        })}`}
                        onPress={onPress}
                        label={`${numberOfComments}`}
                        preventFocusOnPress
                    />
                    <Button
                        kind="secondary"
                        size="small"
                        leadingIcon={Icon.Plus}
                        label="Leave another comment"
                        iconOnly
                        onPress={onPressNewComment}
                    />
                </ButtonGroup>
            );
        }

        return (
            <Button
                kind="secondary"
                data-exclude-from-copy
                size="small"
                leadingIcon={Icon.Comment}
                onPress={onPress}
                iconOnly
                label="Add comment"
                preventFocusOnPress
            />
        );
    }
);
